import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
} from '@material-ui/core';
import { ToggleButton } from '@material-ui/lab';
import {
  FILTERABLE_STATUSES,
  getAvailableScopes,
  getEffectiveStatuses,
  selectStatuses,
  STATUS_GROUPS,
  TripAccess,
  TripFilterState,
  TripPreset,
  TripScope,
} from './filters';
import { TripStatuses } from './types';
import {
  FilterActions,
  FilterBar,
  FilterNote,
  NeedsCount,
  SegmentedControl,
  StatusSelect,
} from './BusinessTrips.css';

const SCOPE_LABELS: Record<TripScope, string> = {
  all: 'BUSINESS_TRIP.FILTER.SCOPE.ALL',
  team: 'BUSINESS_TRIP.FILTER.SCOPE.TEAM',
  mine: 'BUSINESS_TRIP.FILTER.SCOPE.MINE',
};

const PRESET_LABELS: Record<Exclude<TripPreset, 'custom'>, string> = {
  needs: 'BUSINESS_TRIP.FILTER.SHOW.NEEDS_ACTION',
  open: 'BUSINESS_TRIP.FILTER.SHOW.OPEN',
  all: 'BUSINESS_TRIP.FILTER.SHOW.ALL',
};

export interface BusinessTripFilterBarProps {
  access: TripAccess;
  filters: TripFilterState;
  needsCount?: number;
  loading: boolean;
  onChange: (filters: Partial<TripFilterState>) => void;
  actions?: ReactNode;
}

const BusinessTripFilterBar = ({
  access,
  filters,
  needsCount,
  loading,
  onChange,
  actions,
}: BusinessTripFilterBarProps) => {
  const intl = useIntl();
  const message = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id }, values);

  const scopes = getAvailableScopes(access);
  const selectedStatuses =
    filters.preset === 'all'
      ? FILTERABLE_STATUSES
      : getEffectiveStatuses(filters, access);

  const statusLabel = () => {
    if (filters.preset !== 'custom') {
      return message(PRESET_LABELS[filters.preset]);
    }
    if (filters.statuses.length === 1) {
      return message(`BUSINESS_TRIP.STATUS.${filters.statuses[0]}`);
    }
    return message('BUSINESS_TRIP.FILTER.STATUS.COUNT', {
      count: filters.statuses.length,
    });
  };

  const onStatusesChange = (value: unknown) => {
    const selected = (value as (TripStatuses | undefined)[]).filter(
      (status): status is TripStatuses => !!status,
    );
    onChange(selectStatuses(selected, filters.scope, access));
  };

  return (
    <FilterBar>
      {scopes.length > 1 ? (
        <SegmentedControl
          exclusive
          size="small"
          value={filters.scope}
          onChange={(_, scope) => scope && onChange({ scope })}
          aria-label={message('BUSINESS_TRIP.FILTER.SCOPE')}
        >
          {scopes.map((scope) => (
            <ToggleButton
              key={scope}
              value={scope}
              disabled={loading}
              id={`business-trip-scope-${scope}`}
            >
              {message(SCOPE_LABELS[scope])}
            </ToggleButton>
          ))}
        </SegmentedControl>
      ) : (
        <FilterNote>
          {message('BUSINESS_TRIP.FILTER.SCOPE.MINE_ONLY')}
        </FilterNote>
      )}
      <SegmentedControl
        exclusive
        size="small"
        value={filters.preset}
        onChange={(_, preset) => preset && onChange({ preset, statuses: [] })}
        aria-label={message('BUSINESS_TRIP.FILTER.SHOW')}
      >
        {(Object.keys(PRESET_LABELS) as (keyof typeof PRESET_LABELS)[]).map(
          (preset) => (
            <ToggleButton
              key={preset}
              value={preset}
              disabled={loading}
              id={`business-trip-show-${preset}`}
            >
              {message(PRESET_LABELS[preset])}
              {preset === 'needs' && typeof needsCount === 'number' && (
                <NeedsCount>{needsCount}</NeedsCount>
              )}
            </ToggleButton>
          ),
        )}
      </SegmentedControl>
      <FormControl variant="outlined" size="small" disabled={loading}>
        <InputLabel id="business-trip-status-label" shrink>
          {message('BUSINESS_TRIP.FILTER.STATUS')}
        </InputLabel>
        <StatusSelect
          multiple
          input={
            <OutlinedInput
              notched
              label={message('BUSINESS_TRIP.FILTER.STATUS')}
            />
          }
          value={selectedStatuses}
          displayEmpty
          labelId="business-trip-status-label"
          renderValue={statusLabel}
          onChange={(event) => onStatusesChange(event.target.value)}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          }}
        >
          {STATUS_GROUPS.flatMap(({ label, statuses }) => [
            <ListSubheader key={label}>{message(label)}</ListSubheader>,
            ...statuses.map((status) => (
              <MenuItem key={status} value={status} dense>
                <Checkbox
                  size="small"
                  color="primary"
                  checked={selectedStatuses.includes(status)}
                />
                <ListItemText
                  primary={message(`BUSINESS_TRIP.STATUS.${status}`)}
                />
              </MenuItem>
            )),
          ])}
        </StatusSelect>
      </FormControl>
      {actions && <FilterActions>{actions}</FilterActions>}
    </FilterBar>
  );
};

export default BusinessTripFilterBar;
